import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  Grid,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Divider
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { getConnections, createConnection, updateConnection, deleteConnection, testConnection } from '../../api/api';

function Setup() {
  const { t } = useTranslation();
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testingConnection, setTestingConnection] = useState(false);
  const [alert, setAlert] = useState({ show: false, type: 'success', message: '' });
  const [formData, setFormData] = useState({
    id: null,
    name: '',
    url: '',
    database: '',
    username: '',
    password: '',
    version: ''
  });

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const response = await getConnections();
      setConnections(response.data.data);
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error loading connections');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (type, message) => {
    setAlert({ show: true, type, message });
    setTimeout(() => setAlert({ show: false, type: 'success', message: '' }), 5000);
  };

  const handleOpenDialog = (connection = null) => {
    if (connection) {
      setFormData({
        id: connection.id,
        name: connection.name,
        url: connection.url,
        database: connection.database,
        username: connection.username,
        password: '',
        version: connection.version || ''
      });
    } else {
      setFormData({
        id: null,
        name: '',
        url: '',
        database: '',
        username: '',
        password: '',
        version: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleTestConnection = async () => {
    setTestingConnection(true);
    try {
      const testData = {
        url: formData.url,
        database: formData.database,
        username: formData.username,
        password: formData.password
      };
      
      // Include connection ID if editing existing connection
      if (formData.id) {
        testData.connection_id = formData.id;
      }
      
      const response = await testConnection(testData);
      showAlert('success', t('setup.testSuccess'));
    } catch (error) {
      showAlert('error', error.response?.data?.error || t('setup.testError'));
    } finally {
      setTestingConnection(false);
    }
  };

  const handleSaveConnection = async () => {
    try {
      if (formData.id) {
        await updateConnection(formData.id, formData);
        showAlert('success', 'Connection updated successfully');
      } else {
        await createConnection(formData);
        showAlert('success', 'Connection created successfully');
      }
      handleCloseDialog();
      loadConnections();
    } catch (error) {
      showAlert('error', error.response?.data?.error || 'Error saving connection');
    }
  };

  const handleDeleteConnection = async (id) => {
    if (window.confirm('Are you sure you want to delete this connection?')) {
      try {
        await deleteConnection(id);
        showAlert('success', 'Connection deleted successfully');
        loadConnections();
      } catch (error) {
        showAlert('error', error.response?.data?.error || 'Error deleting connection');
      }
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">{t('setup.title')}</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
        >
          {t('setup.addConnection')}
        </Button>
      </Box>

      {alert.show && (
        <Alert severity={alert.type} sx={{ mb: 2 }}>
          {alert.message}
        </Alert>
      )}

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('setup.connectionsList')}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : connections.length === 0 ? (
            <Typography color="text.secondary" align="center" p={3}>
              {t('setup.noConnections')}
            </Typography>
          ) : (
            <List>
              {connections.map((conn) => (
                <ListItem key={conn.id} divider>
                  <ListItemText
                    primary={conn.name}
                    secondary={`${conn.url} - ${conn.database}`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton edge="end" onClick={() => handleOpenDialog(conn)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton edge="end" onClick={() => handleDeleteConnection(conn.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {formData.id ? t('setup.editConnection') : t('setup.addConnection')}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('setup.connectionName')}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={t('setup.url')}
                name="url"
                value={formData.url}
                onChange={handleInputChange}
                placeholder="https://demo.odoo.com"
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('setup.database')}
                name="database"
                value={formData.database}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('setup.version')}
                name="version"
                value={formData.version}
                onChange={handleInputChange}
                placeholder="14.0, 15.0, 16.0..."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('setup.username')}
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="password"
                label={t('setup.password')}
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required={!formData.id}
              />
            </Grid>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                startIcon={testingConnection ? <CircularProgress size={20} /> : <CheckCircleIcon />}
                onClick={handleTestConnection}
                disabled={testingConnection || !formData.url || !formData.database || !formData.username || (!formData.password && !formData.id)}
              >
                {testingConnection ? t('setup.testing') : t('setup.testConnection')}
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('setup.cancel')}</Button>
          <Button
            onClick={handleSaveConnection}
            variant="contained"
            disabled={!formData.name || !formData.url || !formData.database || !formData.username}
          >
            {t('setup.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Setup;
